import { parse } from 'url';
import { ParsedUrlQuery } from 'querystring';

import { ValueOfRecord } from 'ramda';
import { InitOptions } from 'i18next';
import React, { useEffect } from 'react';
import Button from '@sizeid/commons/components/Button';
import { INDEX, ACCOUNT_INDEX } from '@sizeid/shared/constants/routes';
import { NextPageContext } from 'next';
import { useRouter } from 'next/router';

import { Session } from 'types';
import app, { PageType } from 'components/app';
import Page, { Section, Wrapper } from 'components/Page';
import RouteLink from 'components/RouteLink';
import { Router, useTranslation } from 'lib/i18n';

import { SignIndexInitialProps, SignIndexProps } from './types';
import { advisorSectionStyle, sectionStyle } from './styles';
import SignForms from './SignForms';

const decodeUri = (returnUrl: ValueOfRecord<ParsedUrlQuery>): string => {
  const uri = decodeURIComponent(String(returnUrl || ''));
  return uri.replace('#', '%23');
};

const SignIndex: PageType<SignIndexProps, SignIndexInitialProps> = props => {
  const router = useRouter();
  const { t, i18n } = useTranslation('pages');

  // For use inside advisor
  const { returnUrl, advisorLanguage } = router.query;

  // Change to same language that advisor url specified
  useEffect(() => {
    const changeLanguage = async (advisorLanguage: string): Promise<void> => {
      if (
        (
          i18n.options as InitOptions & { allLanguages: string[] }
        ).allLanguages.includes(advisorLanguage)
      ) {
        await i18n.changeLanguage(advisorLanguage);
      }
    };
    if (advisorLanguage !== undefined && typeof advisorLanguage === 'string') {
      let changeTo = advisorLanguage;
      if (advisorLanguage.includes('_')) {
        const splitAdvisorLanguage = advisorLanguage.split('_');
        changeTo = splitAdvisorLanguage[splitAdvisorLanguage.length - 1];
      }
      changeLanguage(changeTo);
    }
  }, [advisorLanguage]);

  return props.advisor ? (
    <Section css={advisorSectionStyle}>
      <>
        <SignForms {...props} />
        <Button link={decodeUri(returnUrl)} outline secondary>
          {t('sign.continueWithoutSizeID')}
        </Button>
      </>
    </Section>
  ) : (
    <Page title={t('sign.title')} footer noHeader noPadding>
      <Section css={sectionStyle}>
        <Wrapper>
          <>
            <SignForms {...props} />
            <RouteLink href={INDEX.route} passHref>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
              <a>
                <Button outline secondary>
                  {t('sign.backToHomepage')}
                </Button>
              </a>
            </RouteLink>
          </>
        </Wrapper>
      </Section>
    </Page>
  );
};

SignIndex.getInitialProps = async (
  context: NextPageContext,
  session: Session,
): Promise<SignIndexInitialProps> => {
  const { req, res } = context;

  if (!session || !session.user) {
    const url = req ? parse(`${req.url}`, true) : Router;
    return {
      advisor: url && url.query && 'advisor' in url.query,
    };
  }

  if (req && res) {
    res.writeHead(302, {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      Location: `/${req.language || DEFAULT_LOCALE}${ACCOUNT_INDEX.route}`,
    });

    res.end();
  } else {
    Router.push(ACCOUNT_INDEX.page);
  }

  return {};
};

export default app(SignIndex);
